import React, { useState, useEffect } from 'react';
import { coreValuesAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';

const CoreValuesManager = () => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    order: 0,
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchValues();
  }, []);

  const fetchValues = async () => {
    try {
      const response = await coreValuesAPI.getAllAdmin();
      setValues(response.data);
    } catch (err) {
      setError('Failed to fetch core values');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await coreValuesAPI.update(editing._id, formData);
        setSuccess('Core value updated successfully');
      } else {
        await coreValuesAPI.create(formData);
        setSuccess('Core value created successfully');
      }
      resetForm();
      fetchValues();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save core value');
    }
  };

  const handleEdit = (value) => {
    setEditing(value);
    setFormData({
      title: value.title || '',
      image: value.image || '',
      description: value.description || '',
      order: value.order || 0,
      isActive: value.isActive !== undefined ? value.isActive : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this core value?')) return;
    try {
      await coreValuesAPI.delete(id);
      setSuccess('Core value deleted successfully');
      fetchValues();
    } catch (err) {
      setError('Failed to delete core value');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      image: '',
      description: '',
      order: 0,
      isActive: true,
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="core-values-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Core Value' : 'Add New Core Value'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData({ ...formData, image: url })}
            label="Image"
          />
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Core Values</h2>
        {values.length === 0 ? (
          <div className="empty-state">No core values found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Image</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {values.map((value) => (
                <tr key={value._id}>
                  <td>{value.title}</td>
                  <td>
                    {value.image && (
                      <img src={value.image} alt={value.title} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                    )}
                  </td>
                  <td>{value.order}</td>
                  <td>{value.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(value)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(value._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CoreValuesManager;
